import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import { MdArrowBack as ArrowBackIcon } from 'react-icons/md';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useNavigate, useLocation } from '@tanstack/react-router';
import type { TContentBlockBase } from '@onesource/schemas';

interface LocationState {
  block?: TContentBlockBase;
  lessonId?: string;
}

/**
 * Format duration in seconds to human-readable format
 */
const formatDuration = (seconds: number | null): string | null => {
  if (!seconds) {
    return null;
  }
  const mins = Math.floor(seconds / 60);
  const hrs = Math.floor(mins / 60);
  if (hrs > 0) {
    return `${hrs}h ${mins % 60}m`;
  }
  return `${mins} min`;
};

/**
 * Video Player Page
 * Full-page video player for Vimeo content blocks
 */
export const VideoPlayerPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | undefined;

  const block = state?.block;

  // Loading state if no block data (shouldn't happen normally)
  if (!block) {
    return (
      <Box p={3}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Video data not available. Please navigate from a lesson.
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate({ to: '/community-hub' })}>
          Back to Community Hub
        </Button>
      </Box>
    );
  }

  // Check if it's a valid video block
  if (!block.vimeoId) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ mb: 2 }}>
          This content block does not have a video.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={{ xs: 2, md: 4 }}>
      {/* Video Header */}
      <Box mb={3}>
        <Typography variant="h4" gutterBottom>
          {block.title || 'Video'}
        </Typography>
        {block.vimeoDurationSeconds && (
          <Chip
            icon={<AccessTimeIcon />}
            label={formatDuration(block.vimeoDurationSeconds)}
            size="small"
            variant="outlined"
            sx={{ mr: 1 }}
          />
        )}
      </Box>

      {/* Video Player */}
      <Box
        sx={{
          position: 'relative',
          paddingTop: '56.25%', // 16:9 aspect ratio
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor: 'black',
          mb: 3,
          boxShadow: 3,
        }}
      >
        <iframe
          title={block.title || 'Vimeo Video'}
          src={`https://player.vimeo.com/video/${block.vimeoId}?autoplay=0`}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: 'none',
          }}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
        />
      </Box>

      {/* Description */}
      {block.body && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Description
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {block.body}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
